import { UseFormMethods } from 'react-hook-form';
import { REGEX_PHONE } from 'shared-it-appmod-ui';
import yup from 'src/libraries/validator.library';
import Moment from 'src/libraries/moment.library';
import Lang from 'src/libraries/language';
import {
  DEFAULT_EMPLOYEE_NUMBER,
  DroneInternalFlightType,
  FlightCategory,
  REGEX_MILITARY_TIME,
} from 'src/constants';
import { FileSchema } from './file.model';

const notEmployee = <T extends yup.StringSchema>(type: T, rule?: any): T =>
  type.when('isEmployee', {
    is: false,
    then: rule || type,
  });

export const RequestFiltersSchema = yup
  .object({
    districts: yup.array().of(yup.string()),
    requestStatuses: yup.array().of(yup.string()),
    flightTypes: yup.array().of(yup.string()),
    operatorNames: yup.array().of(yup.string()),
    tailNumbers: yup.array().of(yup.string()),
    requestorNames: yup.array().of(yup.string()),
    tarStatuses: yup.array().of(yup.string()),
  })
  .defined();

export const RequestCircuitSchema = yup
  .object({
    circuitId: yup.number().nullable(),
    flightId: yup.number().positive().nullable(),
    value: yup.string().required(),
  })
  .defined();

const AccountingShape = {
  workOrder: yup.string().nullable(),
  ioNo: yup.string().trim().required(Lang.MSG_ERROR_IO_NUMBER_REQUIRED),
  planCategory: yup.string().nullable(),
};

const AccountingListShape = {
  workOrder: yup.string().nullable(),
  ioNo: yup.string().trim().nullable(),
  planCategory: yup.string().nullable(),
};

const RequestPassengerFormSchema = yup
  .object({
    name: yup.string().required(),
    empNo: yup
      .string()
      .requiredIfEmpty(['name'])
      .test(
        'noDuplicates',
        Lang.MSG_EMPLOYEE_NUMBER_ERROR_DUPLICATE,
        // eslint-disable-next-line func-names
        function (value) {
          const list = (
            (this as any).from[1].value.passengerInfos ?? []
          ).reduce(
            (a, c) =>
              a.set(
                c.empNo,
                c.empNo !== DEFAULT_EMPLOYEE_NUMBER
                  ? Number(a.get(c.empNo) || 0) + 1
                  : 0
              ),
            new Map()
          );

          return list.get(value) < 2;
        }
      ),
    weight: yup.number().required().notNaN(),
  })
  .defined();

const RequestPassengerSchema = yup
  .object({
    name: yup.string().nullable(),
    empNo: yup.string().nullable(),
    weight: yup.string().nullable(),
    flightId: yup.number(),
    passengerId: yup.number(),
  })
  .defined();

export const RequestInformationSchema = yup
  .object({
    requestId: yup.number().required(),
    requestorNo: yup.string().nullable(),
    requestorName: yup.string().nullable(),
    requestorDept: yup.string().nullable(),
    requestorEmail: yup.string().nullable(),
    requestorDistrict: yup.string().nullable(),
    requestorPhone: yup.string().nullable(),
    requestorJobTitle: yup.string().nullable(),
    fieldContactFlag: yup.boolean().nullable(),
    fieldNo: yup.string().nullable(),
    fieldName: yup.string().nullable(),
    fieldDept: yup.string().nullable(),
    fieldDistrict: yup.string().nullable(),
    pocNo: yup.string().nullable(),
    pocName: yup.string().nullable(),
    pocDept: yup.string().nullable(),
    pocEmail: yup.string().nullable(),
    pocDistrict: yup.string().nullable(),
    pocPhone: yup.string().nullable(),
    pocJobTitle: yup.string().nullable(),
    pocManagerName: yup.string().nullable(),
    pocManagerEmail: yup.string().nullable(),
    pocCompany: yup.string().nullable(),
    finalFieldDept: yup.string().nullable(),
    finalFieldDistrict: yup.string().nullable(),
    fieldPhone: yup.string().nullable(),
    fieldCompany: yup.string().nullable(),
    tempFlightDate: yup.date().nullable(),
    flightDate: yup
      .date()
      .transform((value, originalValue) => {
        return Moment(String(originalValue).replace(/T.*$/, '')).toDate();
      })
      .nullable(),
    flightEndDate: yup.date().nullable(),
    startTime: yup.string().nullable(),
    endTime: yup.string().nullable(),
    flightDuration: yup.string().nullable(),
    releaseTime: yup.string().nullable(),
    flightFlag: yup.number().required(),
    costCenter: yup.string().nullable(),

    accounting: yup.array().of(yup.object(AccountingShape).defined()).defined(),

    requestStatus: yup.string().nullable(),
    tarStatus: yup.string().nullable(),
    reason: yup.string().nullable(),
    reasonBy: yup.string().nullable(),
    reasonDate: yup.date().nullable(),
    requestDate: yup.date().nullable(),
    surveyFlag: yup.boolean().nullable(),
    riskScore: yup.string().nullable(),
    managerName: yup.string().nullable(),
    managerEmail: yup.string().nullable(),
  })
  .defined();

export const RequestInformationFormSchema = yup
  .object()
  .shape({
    requestId: yup.number(),
    requestorNo: yup
      .string()
      .required('Employee name or number must be entered.'),
    pocNo: yup
      .string()
      .required('Employee name or number must be entered.'),
    requestorName: yup.string(),
    requestorDept: yup.string().nullable(),
    requestorDistrict: yup.string().nullable(),
    requestorPhone: yup
      .string()
      .matches(REGEX_PHONE, Lang.MSG_PHONE_NUMBER_ERROR)
      .required(),
    pocPhone: yup
      .string()
      .matches(REGEX_PHONE, Lang.MSG_PHONE_NUMBER_ERROR)
      .required(),
    requestorJobTitle: yup.string().nullable(),
    fieldContactFlag: yup.boolean().required(Lang.MSG_ERROR_MAKE_SELECTION),
    isEmployee: yup.boolean().when(['fieldContactFlag', 'requestId'], {
      is: (fieldContactFlag, requestId) =>
        fieldContactFlag === false && !requestId,
      then: yup.boolean().required(),
    }),
    fieldNo: yup.string().when('fieldContactFlag', {
      is: false,
      then: yup.string().required(),
    }),
    fieldName: notEmployee(yup.string(), yup.string().required()),
    fieldDept: notEmployee(yup.string()).nullable(),
    fieldDistrict: notEmployee(yup.string()).nullable(),
    finalFieldDept: notEmployee(yup.string()).nullable(),
    finalFieldDistrict: notEmployee(yup.string()).nullable(),
    fieldPhone: yup
      .string()
      .matches(REGEX_PHONE, Lang.MSG_PHONE_NUMBER_ERROR)
      .when('fieldContactFlag', {
        is: false,
        then: yup.string().required(),
      }),
    fieldCompany: notEmployee(yup.string()).nullable(),
    tempFlightDate: yup.date(),
    flightDate: yup.date().required(),
    flightEndDate: yup.date().nullable(),
    // .when('tempFlightDate', {
    //   is: (temp) => temp,
    //   then: yup
    //     .date()
    //     .min(yup.ref('tempFlightDate'), Lang.MSG_DATE_ERROR)
    //     .typeError(Lang.MSG_DATE_ERROR),
    // }),
    startTime: yup
      .string()
      // .test(
      //   'startTimeCheck',
      //   Lang.MSG_TIME_START_ELAPSE_ERROR,
      //   // eslint-disable-next-line func-names
      //   function (value) {
      //     const { parent } = this;

      //     if (
      //       parent.flightDate &&
      //       !parent.requestId &&
      //       value &&
      //       /\d/.test(value)
      //     ) {
      //       const startDate = Moment(
      //         `${Moment(parent.flightDate).format('YYYY-MM-DD')} ${value}`,
      //         'YYYY-MM-DD HH:mm'
      //       );

      //       const dateCheck =
      //         Moment(parent.tempFlightDate).isSameOrAfter(Moment(), 'day') &&
      //         startDate.isBefore(Moment());

      //       if (startDate.isValid() && dateCheck) {
      //         return false;
      //       }
      //     }

      //     return true;
      //   }
      // )
      .required(),
    endTime: yup.string().nullable(),
    flightDuration: yup.string().required(Lang.MSG_TIME_DURATION_ERROR),
    releaseTime: yup.string().nullable(),
    flightFlag: yup.number().required(Lang.MSG_ERROR_MAKE_SELECTION), // Flight category ID
    costCenter: yup
  .string()
  .nullable()
  .test('isNumeric', Lang.MSG_ERROR_COST_CENTER_DIGIT, (val) => {
    if (!val || val.length === 0) {
      return true;
    }
    return /^(\d{4}-)?\d+$/.test(val);
  }),
    accounting: yup
      .array()
      .of(
        yup
          .object({
            workOrder: yup.string().nullable().default(null),
            ioNo: yup.string().trim().required(Lang.MSG_ERROR_IO_NUMBER_REQUIRED),
            planCategory: yup.string().nullable(),
          })
          .defined()
      )
      .min(1)
      .required(),

    requestStatus: yup.string().when('requestId', {
      is: (value) => !!value,
      then: yup.string().required(),
    }),
    tarStatus: yup.string().nullable().default(null),
    reason: yup.string().nullable(),
    reasonBy: yup.string().nullable(),
    reasonDate: yup.date().nullable(),
    requestDate: yup.date().nullable(),
    surveyFlag: yup.boolean().nullable(),
    riskScore: yup.string().nullable(),
  })
  .defined();

export const RequestPassengerFlightFormSchema = yup
  .object({
    flightType: yup.string().required(),
    flightDestination: yup.string().required(),
    flightDept: yup.string().required(),
    polygon: yup.string().nullable(),
    purpose: yup
      .string()
      .required(Lang.MSG_ERROR_PURPOSE_REQUIRED)
      .max(200, Lang.MSG_ERROR_PURPOSE_MAX_LIMIT),
    flightLocation: yup.string().required(),
    project: yup.string().nullable(),
    nightFlag: yup.boolean().required(Lang.MSG_ERROR_MAKE_SELECTION),

    helmetFlag: yup.boolean().required(Lang.MSG_ERROR_MAKE_SELECTION),
    basketFlag: yup.boolean().required(Lang.MSG_ERROR_MAKE_SELECTION),
    basketWeight: yup.string().nullable().when('basketFlag', {
      is: true,
      then: yup.string().required(),
    }),
    operatorId: yup.number().nullable(),
    operatorName: yup.string().nullable(),
    pilotName: yup.string().nullable(),
    pilotPhone: yup
      .string()
      .matches(REGEX_PHONE, Lang.MSG_PHONE_NUMBER_ERROR)
      .nullable(),
    primeContractor: yup.string().required(),
    primeName: yup.string().required(),
    primePhone: yup
      .string()
      .matches(REGEX_PHONE, Lang.MSG_PHONE_NUMBER_ERROR)
      .required(),
    primeEmail: yup.string().required(),
    hazmatFlag: yup.boolean().required(Lang.MSG_ERROR_MAKE_SELECTION),
    passengerComments: yup.string().nullable(),

    culturalFlag: yup.boolean().required(Lang.MSG_ERROR_MAKE_SELECTION),

    culturalFiles: yup.array().of(FileSchema.clone()).nullable(),

    sensitiveFlag: yup.boolean().required(Lang.MSG_ERROR_MAKE_SELECTION),
    sensitiveCommunity: yup.string().nullable(),
    droneArea: yup.string().nullable(),
    sensitiveCity: yup.string().nullable(),
    sensitiveFiles: yup.array().of(FileSchema.clone()).nullable(),
    aomFlag: yup.boolean().nullable(),
    asdRadioFlag: yup.boolean().nullable(),
    asdTrackingFlag: yup.boolean().nullable(),
    extraEquipmentFlag: yup.boolean().required(Lang.MSG_ERROR_MAKE_SELECTION),
    flightAreaFiles: yup.array().min(1).required().of(FileSchema.clone()),
    equipmentInfos: yup
      .array()
      .of(
        yup.object({
          type: yup.string().required(),
          weight: yup.number().required().notNaN(),
        })
      )
      .requiredIfValue('extraEquipmentFlag', true),

    passengerInfos: yup.array().of(RequestPassengerFormSchema.clone()),
    circuitInfos: yup.array().of(RequestCircuitSchema.clone()),
  })
  .defined();

export const RequestPassengerFlightSchema = yup
  .object({
    flightType: yup.string().nullable(),
    flightDestination: yup.string().nullable(),
    flightDept: yup.string().nullable(),
    polygon: yup.string().nullable(),
    isOldFlight: yup.boolean().nullable(),
    isOldSCG: yup.boolean().nullable(),
    flightLocation: yup.string().nullable(),
    project: yup.string().nullable(),
    purpose: yup.string().nullable(),
    helmetFlag: yup.boolean().nullable(),
    basketFlag: yup.boolean().nullable(),
    basketWeight: yup.string().nullable(),
    hazmatFlag: yup.boolean().nullable(),
    passengerComments: yup.string().nullable(),
    pickupLocation: yup.string().nullable(),
    destination: yup.string().nullable(),
    coordinates: yup.string().nullable(),
    nightFlag: yup.boolean().nullable(),

    culturalFlag: yup.boolean().nullable(),
    culturalFiles: yup.array().of(FileSchema.clone()),

    sensitiveFlag: yup.boolean().nullable(),
    sensitiveCommunity: yup.string().nullable(),
    droneArea: yup.string().nullable(),
    sensitiveCity: yup.string().nullable(),
    sensitiveFiles: yup.array().of(FileSchema.clone()),

    extraEquipmentFlag: yup.boolean().nullable(),
    flightAreaFiles: yup.array().of(FileSchema.clone()),
    equipmentInfos: yup.array().of(
      yup.object({
        type: yup.string(),
        weight: yup.number(),
      })
    ),
    district: yup.string().nullable(),
    operatorId: yup.number().nullable(),
    operatorName: yup.string().nullable(),
    pilotName: yup.string().nullable(),
    pilotPhone: yup
      .string()
      .matches(REGEX_PHONE, Lang.MSG_PHONE_NUMBER_ERROR)
      .nullable(),
    primeContractor: yup.string().nullable(),
    primeName: yup.string().nullable(),
    primePhone: yup
      .string()
      .matches(REGEX_PHONE, Lang.MSG_PHONE_NUMBER_ERROR)
      .nullable(),
    primeEmail: yup.string().nullable(),
    comments: yup.string().nullable(),
    aomFlag: yup.boolean().nullable(),
    asdRadioFlag: yup.boolean().nullable(),
    asdTrackingFlag: yup.boolean().nullable(),
    passengerInfos: yup.array().of(RequestPassengerSchema.clone()),
    circuitInfos: yup.array().of(RequestCircuitSchema.clone()),
  })
  .defined();

const RequestLandingZoneSchema = yup
  .object({
    flightId: yup.number(),
    landingId: yup.number(),
    uploadId: yup.number().nullable(),
    address: yup.string().nullable(),
    coordinate: yup.string().required(),
    file: FileSchema.clone().optional(true, null),
  })
  .defined();

const StructureIdSchema = yup
  .object({
    structureId: yup.string().required(),
    flightId: yup.number(),
    structureKey: yup.number(),
  })
  .defined();

const CNDWONumberSchema = yup
  .object({
    cndWONumber: yup.string().nullable().default(null),
    flightId: yup.number(),
    cndWOId: yup.number(),
  })
  .defined();

export const RequestConstructionContractorFormSchema = yup
  .object({
    projectTitle: yup.string().nullable(),
    circuit: yup.string().required(),
    structureInfos: yup.array().of(StructureIdSchema.clone()).min(1).required(),
    structureFiles: yup.array().of(FileSchema.clone()),
    landingInfos: yup.array().of(RequestLandingZoneSchema.clone()).min(1),
    purpose: yup
      .string()
      .required(Lang.MSG_ERROR_PURPOSE_REQUIRED)
      .max(200, Lang.MSG_ERROR_PURPOSE_MAX_LIMIT),
    nightFlag: yup.boolean().required(Lang.MSG_ERROR_MAKE_SELECTION),

    culturalFlag: yup.boolean().required(Lang.MSG_ERROR_MAKE_SELECTION),
    culturalFiles: yup.array().of(FileSchema.clone()),
    sensitiveFlag: yup.boolean().required(Lang.MSG_ERROR_MAKE_SELECTION),
    sensitiveCommunity: yup.string().nullable(),
    droneArea: yup.string().nullable(),
    sensitiveCity: yup.string().nullable(),
    sensitiveFiles: yup.array().of(FileSchema.clone()),

    operatorId: yup.number().nullable(),
    operatorName: yup.string().nullable(),
    pilotName: yup.string().nullable(),
    pilotPhone: yup
      .string()
      .matches(REGEX_PHONE, Lang.MSG_PHONE_NUMBER_ERROR)
      .nullable(),
    primeContractor: yup.string().required(),
    primeName: yup.string().required(),
    primePhone: yup
      .string()
      .matches(REGEX_PHONE, Lang.MSG_PHONE_NUMBER_ERROR)
      .required(),
    primeEmail: yup.string().required(),
    liftFlag: yup.boolean().required(Lang.MSG_ERROR_MAKE_SELECTION),
    liftFiles: yup.array().of(FileSchema.clone()),

    complianceFlag: yup.boolean().required(Lang.MSG_ERROR_MAKE_SELECTION),

    lineFlag: yup.boolean().required(Lang.MSG_ERROR_MAKE_SELECTION),

    hecFlag: yup.boolean().required(Lang.MSG_ERROR_MAKE_SELECTION),
    hecFiles: yup.array().of(FileSchema.clone()),

    congestedFlag: yup.boolean().required(Lang.MSG_ERROR_MAKE_SELECTION),
    congestedFiles: yup.array().of(FileSchema.clone()),

    sockFlag: yup.boolean().required(Lang.MSG_ERROR_MAKE_SELECTION),
    platformFlag: yup.boolean().required(Lang.MSG_ERROR_MAKE_SELECTION),
    skidFlag: yup.boolean().required(Lang.MSG_ERROR_MAKE_SELECTION),
    flightAreaFiles: yup.array().min(1).required().of(FileSchema.clone()),

    aircraftType: yup.string().required(),
    tailNo: yup.string().required(),
    trackerFlag: yup.boolean(),
    comments: yup.string().nullable(),

    requestId: yup.number(),
    flightId: yup.number(),
    aviationContractor: yup.string().nullable(),
    aviationName: yup.string().nullable(),
    aviationPhone: yup.string().nullable(),
    endDate: yup.date().nullable(),
    frequency: yup.string().nullable(),
    passengerCount: yup.number().nullable(),
    passengerCountFinal: yup.number().nullable(),
    startDate: yup.date().nullable(),
    structure: yup.string().nullable(),
  })
  .defined();

export const RequestConstructionContractorSchema = yup
  .object({
    projectTitle: yup.string().nullable(),
    circuit: yup.string().nullable(),
    structureInfos: yup.array().of(
      yup
        .object({
          structureId: yup.string().nullable(),
          flightId: yup.number().nullable(),
          structureKey: yup.number().nullable(),
        })
        .defined()
    ),
    structureFiles: yup.array().of(FileSchema.clone()),
    landingInfos: yup.array().of(RequestLandingZoneSchema.clone()),
    purpose: yup.string().nullable(),
    nightFlag: yup.boolean().nullable(),

    culturalFlag: yup.boolean().nullable(),
    culturalFiles: yup.array().of(FileSchema.clone()),
    sensitiveFlag: yup.boolean().nullable(),
    sensitiveCommunity: yup.string().nullable(),
    droneArea: yup.string().nullable(),
    sensitiveCity: yup.string().nullable(),
    sensitiveFiles: yup.array().of(FileSchema.clone()),

    operatorId: yup.number().nullable(),
    operatorName: yup.string().nullable(),
    pilotName: yup.string().nullable(),
    pilotPhone: yup.string().nullable(),
    primeContractor: yup.string().nullable(),
    primeName: yup.string().nullable(),
    primePhone: yup.string().nullable(),
    primeEmail: yup.string().nullable(),

    liftFlag: yup.boolean().nullable(),
    liftFiles: yup.array().of(FileSchema.clone()),

    complianceFlag: yup.boolean().nullable(),

    lineFlag: yup.boolean().nullable(),

    hecFlag: yup.boolean().nullable(),
    hecFiles: yup.array().of(FileSchema.clone()),

    congestedFlag: yup.boolean(),
    congestedFiles: yup.array().of(FileSchema.clone()),

    sockFlag: yup.boolean().nullable(),
    platformFlag: yup.boolean().nullable(),
    skidFlag: yup.boolean().nullable(),

    aircraftType: yup.string().nullable(),
    tailNo: yup.string().nullable(),
    trackerFlag: yup.boolean().nullable(),
    comments: yup.string().nullable(),

    requestId: yup.number().nullable(),
    flightId: yup.number().nullable(),
    aviationContractor: yup.string().nullable(),
    aviationName: yup.string().nullable(),
    aviationPhone: yup.string().nullable(),
    endDate: yup.date().nullable(),
    frequency: yup.string().nullable(),
    passengerCount: yup.number().nullable(),
    passengerCountFinal: yup.number().nullable(),
    startDate: yup.date().nullable(),
    structure: yup.string().nullable(),
    flightAreaFiles: yup.array().of(FileSchema.clone()),
  })
  .defined();

export const RequestPassengerContractorFlightFormSchema = yup
  .object({
    flightId: yup.number(),
    flightType: yup.string().required(),
    project: yup.string().nullable(),
    pickupLocation: yup.string().required(),
    destination: yup.string().required(),
    coordinates: yup.string().required(),
    nightFlag: yup.boolean().required(Lang.MSG_ERROR_MAKE_SELECTION),
    aomFlag: yup.boolean().required(Lang.MSG_ERROR_MAKE_SELECTION),
    asdTrackingFlag: yup.boolean().required(Lang.MSG_ERROR_MAKE_SELECTION),
    asdRadioFlag: yup.boolean().required(Lang.MSG_ERROR_MAKE_SELECTION),
    district: yup.string().required(),
    operatorId: yup.number().nullable(),
    operatorName: yup.string().nullable(),
    pilotName: yup.string().nullable(),
    pilotPhone: yup
      .string()
      .matches(REGEX_PHONE, Lang.MSG_PHONE_NUMBER_ERROR)
      .nullable(),
    primeContractor: yup.string().required(),
    primeName: yup.string().required(),
    primePhone: yup
      .string()
      .matches(REGEX_PHONE, Lang.MSG_PHONE_NUMBER_ERROR)
      .required(),
    primeEmail: yup.string().required(),
    comments: yup.string().nullable(),
    passengerInfos: yup.array().of(RequestPassengerFormSchema.clone()),
    flightAreaFiles: yup.array().min(1).required().of(FileSchema.clone()),
    circuitInfos: yup
      .array()
      .of(RequestCircuitSchema.clone())
      .min(1)
      .required(),
  })
  .defined();

export const RequestPassengerContractorFlightSchema = yup
  .object({
    aomFlag: yup.boolean().nullable(),
    asdRadioFlag: yup.boolean().nullable(),
    asdTrackingFlag: yup.boolean().nullable(),
    circuitInfos: yup.array().of(RequestCircuitSchema.clone()),
    comments: yup.string().nullable(),
    coordinates: yup.string().nullable(),
    destination: yup.string().nullable(),
    flightId: yup.number().nullable(),
    flightType: yup.string().nullable(),
    lastModifiedByName: yup.string().nullable(),
    lastModifiedDate: yup.string().nullable(),
    lastModifiedEmpNo: yup.string().nullable(),
    operatorId: yup.number().nullable(),
    operatorName: yup.string().nullable(),
    passengerInfos: yup.array().of(RequestPassengerSchema.clone()),
    pickupLocation: yup.string(),
    pilotName: yup.string().nullable(),
    pilotPhone: yup.string().nullable(),
    primeContractor: yup.string().nullable(),
    primeName: yup.string().nullable(),
    primePhone: yup.string().nullable(),
    primeEmail: yup.string().nullable(),
    project: yup.string().nullable(),
    requestId: yup.number().nullable(),
    district: yup.string().nullable(),

    flightAreaFiles: yup.array().of(FileSchema.clone()),
    nightFlag: yup.boolean().nullable(),
  })
  .defined();

const RequestDroneFormShape = {
  flightType: yup.string().required(),
  destination: yup.string().required(),
  asset: yup.string().nullable(),
  circuit: yup.string().required(),
  purpose: yup
    .string()
    .required(Lang.MSG_ERROR_PURPOSE_REQUIRED)
    .max(200, Lang.MSG_ERROR_PURPOSE_MAX_LIMIT),
  sensitiveFlag: yup.boolean().nullable(),
  sensitiveCommunity: yup.string().nullable(),
  droneArea: yup.string().nullable(),
  sensitiveCity: yup.string().nullable(),
  sensitiveFiles: yup.array().of(FileSchema.clone()).nullable(),
  nightFlag: yup.boolean().required(Lang.MSG_ERROR_MAKE_SELECTION),
  flightAreaFiles: yup.array().min(1).required().of(FileSchema.clone()),
  isOldFlight: yup.boolean().nullable(),
  airspaceFlag: yup
    .boolean()
    .nullable(),

  airspaceFiles: yup.array().of(FileSchema.clone()).nullable(),

  culturalFlag: yup
    .boolean()
    .nullable()
    .test('checkIfUndefined', Lang.MSG_ERROR_MAKE_SELECTION, (value) => {
      if (value === undefined) {
        return false;
      }

      return true;
    }),
  culturalFiles: yup.array().of(FileSchema.clone()).nullable(),

  landFlag: yup
    .boolean()
    .nullable()
    .test('checkIfUndefined', Lang.MSG_ERROR_MAKE_SELECTION, (value) => {
      if (value === undefined) {
        return false;
      }

      return true;
    }),
  landFiles: yup.array().of(FileSchema.clone()),

  safetyFlag: yup
    .boolean()
    .nullable()
    .test('checkIfUndefined', Lang.MSG_ERROR_MAKE_SELECTION, (value) => {
      if (value === undefined) {
        return false;
      }

      return true;
    }),
  safetyFiles: yup.array().of(FileSchema.clone()),
  jobDesc: yup.string().nullable(),
  comments: yup.string().nullable(),
};

const CND_REQUIRED_DRONE_FLIGHT_TYPES = new Set(
  Object.values(DroneInternalFlightType).map((v) => v.toLowerCase())
);

const isCndRequiredForDrone = (
  sensitiveFlag?: boolean,
  flightType?: string | null
) => {
  if (!sensitiveFlag) {
    return false;
  }

  const normalizedFlightType = String(flightType || '').trim().toLowerCase();
  return CND_REQUIRED_DRONE_FLIGHT_TYPES.has(normalizedFlightType);
};

const normalizeCndWONumbers = (value?: Array<any>) => {
  if (!Array.isArray(value)) {
    return value;
  }

  return value
    .filter((item) => !!String(item?.cndWONumber || '').trim())
    .map((item) => ({
      ...item,
      cndWONumber: String(item?.cndWONumber || '').trim(),
    }));
};

export const RequestDroneSDGEFormSchema = yup
  .object()
  .shape({
    district: yup.string().required(),
    project: yup.string().nullable(),
    polygon: yup.string().nullable(),
    cndWONumbers: yup.array().when(['sensitiveFlag', 'flightType'], {
      is: (sensitiveFlag, flightType) =>
        isCndRequiredForDrone(sensitiveFlag, flightType),
      then: yup
        .array()
        .transform((value) => normalizeCndWONumbers(value))
        .of(CNDWONumberSchema.clone())
        .test(
          'cnd-wo-at-least-one',
          Lang.MSG_ERROR_CND_WORK_ORDER_EMPTY,
          function cndWoAtLeastOne(value) {
            const hasValue =
              Array.isArray(value) &&
              value.some((item) => !!String(item?.cndWONumber || '').trim());
            if (hasValue) {
              return true;
            }
            // Point error at [0].cndWONumber so the banner click targets the
            // first WO input (name="droneFlightSDGE.cndWONumbers[0].cndWONumber")
            return this.createError({
              path: `${this.path}[0].cndWONumber`,
              message: Lang.MSG_ERROR_CND_WORK_ORDER_EMPTY,
            });
          }
        )
        .test(
          'cnd-wo-no-duplicates',
          Lang.MSG_ERROR_WORK_ORDER_DUPLICATE,
          function cndWoNoDuplicates(value) {
            if (!Array.isArray(value)) {
              return true;
            }

            const seen = new Map<string, number>();

            for (let i = 0; i < value.length; i += 1) {
              const workOrder = String(value[i]?.cndWONumber || '').trim();

              if (workOrder) {
                const firstIndex = seen.get(workOrder);

                if (firstIndex !== undefined) {
                  return this.createError({
                    path: `${this.path}[${i}].cndWONumber`,
                    message: Lang.MSG_ERROR_WORK_ORDER_DUPLICATE,
                  });
                }

                seen.set(workOrder, i);
              }
            }

            return true;
          }
        )
        .required(Lang.MSG_ERROR_CND_WORK_ORDER_EMPTY),
      otherwise: yup
        .array()
        .transform((value) => normalizeCndWONumbers(value))
        .of(CNDWONumberSchema.clone())
        .nullable(),
    }),
    cndDistrict: yup.string().when(['sensitiveFlag', 'flightType'], {
      is: (sensitiveFlag, flightType) =>
        isCndRequiredForDrone(sensitiveFlag, flightType),
      then: yup.string().required(Lang.MSG_ERROR_MAKE_SELECTION),
      otherwise: yup.string().nullable(),
    }),
    // cndIONumber: yup.string().nullable(),
    ...RequestDroneFormShape,
  })
  .defined();

export const RequestDroneContractorFormSchema = yup
  .object()
  .shape({
    flightOperator: yup.string().required(),
    project: yup.string().required(),
    primeEmail: yup.string().required(),
    primeName: yup.string().required(),
    pilotName: yup.string().required(),
    culturalFlag: yup.boolean().required(Lang.MSG_ERROR_MAKE_SELECTION),
    culturalFiles: yup.array().of(FileSchema.clone()).nullable(),
    landFlag: yup.boolean().required(Lang.MSG_ERROR_MAKE_SELECTION),
    landFiles: yup.array().of(FileSchema.clone()),
    safetyFlag: yup.boolean().required(Lang.MSG_ERROR_MAKE_SELECTION),
    safetyFiles: yup.array().of(FileSchema.clone()),
    livestockFlag: yup.boolean().nullable(),
    dnadeFlag: yup.boolean().nullable(),
    address: yup.string().required(),
    ...RequestDroneFormShape,
  })
  .defined();

const RequestDroneShape = {
  flightId: yup.number().nullable(),
  flightType: yup.string().nullable(),
  asset: yup.string().nullable(),
  circuit: yup.string().nullable(),
  primeEmail: yup.string().nullable(),
  purpose: yup.string().nullable(),
  sensitiveFlag: yup.boolean().nullable(),
  sensitiveCommunity: yup.string().nullable(),
  droneArea: yup.string().nullable(),
  sensitiveCity: yup.string().nullable(),
  sensitiveFiles: yup.array().of(FileSchema.clone()),
  airspaceFlag: yup.boolean().nullable(),
  airspaceFiles: yup.array().of(FileSchema.clone()),
  isOldFlight: yup.boolean().nullable(),
  culturalFlag: yup.boolean().nullable(),
  culturalFiles: yup.array().of(FileSchema.clone()),

  landFlag: yup.boolean().nullable(),
  landFiles: yup.array().of(FileSchema.clone()),

  safetyFlag: yup.boolean().nullable(),
  safetyFiles: yup.array().of(FileSchema.clone()),
  nightFlag: yup.boolean().nullable(),

  jobDesc: yup.string().nullable(),
  flightAreaFiles: yup.array().of(FileSchema.clone()),
  comments: yup.string().nullable(),
  destination: yup.string().nullable(),
};

export const RequestDroneSDGESchema = yup
  .object()
  .shape({
    district: yup.string().required(),
    project: yup.string().nullable(),
    polygon: yup.string().nullable(),
    cndWONumbers: yup.array().of(CNDWONumberSchema.clone()).nullable(),
    cndDistrict: yup.string().nullable(),
    // cndIONumber: yup.string().nullable(),
    ...RequestDroneShape,
  })
  .defined();

export const RequestDroneContractorSchema = yup
  .object()
  .shape({
    flightOperator: yup.string().nullable(),
    project: yup.string().nullable(),
    primeName: yup.string().nullable(),
    pilotName: yup.string().nullable(),
    culturalFlag: yup.boolean().nullable(),
    culturalFiles: yup.array().of(FileSchema.clone()),
    landFlag: yup.boolean().nullable(),
    landFiles: yup.array().of(FileSchema.clone()),
    safetyFlag: yup.boolean().nullable(),
    safetyFiles: yup.array().of(FileSchema.clone()),
    livestockFlag: yup.boolean().nullable(),
    dnadeFlag: yup.boolean().nullable(),
    address: yup.string().nullable(),
    ...RequestDroneShape,
  })
  .defined();

  const RequestPMultiFlightInfoSchema = yup.object({
    multiFlightInfoId: yup.number(),
    requestId: yup.number(),
    flightNo: yup.string().nullable(),
    flightDate: yup.date().required(),
    departureTime: yup
      .string()
      .nullable()
      .matches(REGEX_MILITARY_TIME, Lang.MSG_MILITARY_TIME_ERROR),
    arrivalTime: yup
      .string()
      .nullable()
      .matches(REGEX_MILITARY_TIME, Lang.MSG_MILITARY_TIME_ERROR),
    standByHours: yup.string().nullable(), // Not Required for Drone
    flightHours: yup.string().nullable(), // Not Required for Drone
    flightType: yup.string().nullable(),
    operatorId: yup.number().nullable(),
    operatorName: yup.string().nullable(),
    tailNumber: yup.string().nullable(),
    pilot: yup.string().nullable(),
    landingAssured: yup.string().nullable(), // Not Required for Drone
    flightCost: yup.string().nullable(), // Not Required for Drone
    riskScore: yup.number().nullable(), // Not Required for Drone
  })
.defined();

export const RequestAdminDetailsSchema = yup
  .object({
    adminComments: yup.string().nullable(),
    aerialContact: yup.string().nullable(), // Not Required
    arrivalTime: yup.string().nullable(),
    contractorMissionFlag: yup.boolean().nullable(),
    crew: yup.string().nullable(), // Not Required
    departureTime: yup.string().nullable(),
    dropCount: yup.number().nullable(),
    flightCost: yup.string().nullable(),
    flightDate: yup.date().nullable(),
    flightEndDate: yup.date().nullable(),
    flightHours: yup.string().nullable(),
    flightNo: yup.string().nullable(),
    flightType: yup.string().nullable(),
    incidentNo: yup.number().nullable(), // Not Required
    landingAssured: yup.string().nullable(),
    longRifleApproval: yup.boolean().nullable(),
    missionCompleteTime: yup.string().nullable(), // Not Required
    observedFlag: yup.boolean().nullable(),
    operatorId: yup.number().nullable(),
    operatorName: yup.string().nullable(),
    pic: yup.string().nullable(), // Not Required
    picPhone: yup.string().nullable(), // Not Required
    pilot: yup.string().nullable(),
    recordId: yup.number().nullable(),
    requestId: yup.number().nullable(),
    responsiblity: yup.string().nullable(), // Not Required
    reviewDate: yup.string().nullable(), // Not Required
    reviewer: yup.string().nullable(), // Not Required
    standByHours: yup.string().nullable(),
    statusSentFlag: yup.boolean().nullable(), // Not Required
    tailNumber: yup.string().nullable(),
    noiseMonitoring: yup.boolean().nullable(),
    addFaaWaiverAuth: yup.boolean().nullable(),
    faaInformation: yup.string().nullable(),
  })
  .defined();

export const RequestAdminDetailsFormSchema = yup
  .object({
    flightNo: yup.string().nullable(),
    flightDate: yup.date().required(),
    flightEndDate: yup.date().nullable(),
    flightType: yup.string().nullable(),
    departureTime: yup
      .string()
      .matches(REGEX_MILITARY_TIME, Lang.MSG_MILITARY_TIME_ERROR)
      .nullable(),
    arrivalTime: yup
      .string()
      .matches(REGEX_MILITARY_TIME, Lang.MSG_MILITARY_TIME_ERROR)
      .nullable(),

    flightHours: yup.string().nullable(),
    standByHours: yup.string().nullable(),

    operatorId: yup.number().nullable(),
    operatorName: yup.string().nullable(),
    tailNumber: yup.string().nullable(),
    pilot: yup.string().nullable(),
    landingAssured: yup.string().nullable(),

    flightCost: yup.string().nullable(),
    noiseMonitoring: yup.boolean().nullable(),
    addFaaWaiverAuth: yup.boolean().nullable(),

    observedFlag: yup.boolean().nullable(),
    contractorMissionFlag: yup.boolean().nullable(),
    longRifleApproval: yup.boolean().nullable(),

    adminComments: yup.string().nullable(),
    faaInformation: yup.string().nullable(),
  })
  .defined();

  const RequestPMultiFlightInfoFormSchema = yup.object({
    flightNo: yup.string().required(),
    flightDate: yup.date().required(),
    departureTime: yup
      .string()
      .nullable()
      .matches(REGEX_MILITARY_TIME, Lang.MSG_MILITARY_TIME_ERROR),
    arrivalTime: yup
      .string()
      .nullable()
      .matches(REGEX_MILITARY_TIME, Lang.MSG_MILITARY_TIME_ERROR),
    standByHours: yup.string().nullable(), // Not Required for Drone
    flightHours: yup.string().nullable(), // Not Required for Drone
    flightType: yup.string().nullable(),
    operatorId: yup.number().nullable(),
    operatorName: yup.string().nullable(),
    tailNumber: yup.string().nullable(),
    pilot: yup.string().nullable(),
    landingAssured: yup.string().nullable(), // Not Required for Drone
    flightCost: yup.string().nullable(), // Not Required for Drone
    riskScore: yup.number().nullable(), // Not Required for Drone
  })
.defined();

export const RequestConstructionFormSchema = yup
  .object({
    flightDept: yup.string().required(),
    structureInfos: yup.array().of(StructureIdSchema.clone()).min(1).required(),
    structureFiles: yup.array().of(FileSchema.clone()),
    landingInfos: yup
      .array()
      .of(RequestLandingZoneSchema.clone())
      .min(1)
      .required(),
    purpose: yup
      .string()
      .required(Lang.MSG_ERROR_PURPOSE_REQUIRED)
      .max(200, Lang.MSG_ERROR_PURPOSE_MAX_LIMIT),
    nightFlag: yup.boolean().required(Lang.MSG_ERROR_MAKE_SELECTION),

    culturalFlag: yup.boolean().required(Lang.MSG_ERROR_MAKE_SELECTION),
    culturalFiles: yup.array().of(FileSchema.clone()).nullable(),

    sensitiveFlag: yup.boolean().required(Lang.MSG_ERROR_MAKE_SELECTION),
    sensitiveCommunity: yup.string().nullable(),
    droneArea: yup.string().nullable(),
    sensitiveCity: yup.string().nullable(),
    sensitiveFiles: yup.array().of(FileSchema.clone()).nullable(),

    landingFlag: yup.boolean().required(),

    equipmentFlag: yup.boolean().required(),
    equipmentDescription: yup.string(),

    poleFlag: yup.boolean().required(Lang.MSG_ERROR_MAKE_SELECTION),
    poleCount: yup.number().nullable().when('poleFlag', {
      is: true,
      then: yup.number().required(),
    }),

    ropeFlag: yup.boolean().required(Lang.MSG_ERROR_MAKE_SELECTION),
    ropeSize: yup.string().nullable().when('ropeFlag', {
      is: true,
      then: yup.string().required(),
    }),

    hecFlag: yup.boolean().required(Lang.MSG_ERROR_MAKE_SELECTION),
    crewCount: yup.string().nullable().when('hecFlag', {
      is: true,
      then: yup.string().required(),
    }),
    crewFlag: yup.boolean().required(Lang.MSG_ERROR_MAKE_SELECTION),
    basketFlag: yup.boolean().required(Lang.MSG_ERROR_MAKE_SELECTION),
    hazmatFlag: yup.boolean().required(Lang.MSG_ERROR_MAKE_SELECTION),
    flightAreaFiles: yup.array().of(FileSchema.clone()),
    loadWeight: yup.string().required(),
    comments: yup.string().nullable(),
  })
  .defined();

export const RequestConstructionSchema = yup
  .object({
    flightDept: yup.string().required(),
    structureInfos: yup.array().of(
      yup
        .object({
          structureId: yup.string().nullable(),
          flightId: yup.number().nullable(),
          structureKey: yup.number().nullable(),
        })
        .defined()
    ),
    structureFiles: yup.array().of(FileSchema.clone()),
    landingInfos: yup.array().of(RequestLandingZoneSchema.clone()),
    purpose: yup.string().nullable(),
    nightFlag: yup.boolean().nullable(),

    culturalFlag: yup.boolean(),
    culturalFiles: yup.array().of(FileSchema.clone()),

    sensitiveFlag: yup.boolean(),
    sensitiveCommunity: yup.string().nullable(),
    droneArea: yup.string().nullable(),
    sensitiveCity: yup.string().nullable(),
    sensitiveFiles: yup.array().of(FileSchema.clone()),

    flightAreaFiles: yup.array().of(FileSchema.clone()),
    landingFlag: yup.boolean(),
    equipmentFlag: yup.boolean().nullable(),
    equipmentDescription: yup.string(),
    poleFlag: yup.boolean().nullable(),
    poleCount: yup.number().nullable(),
    ropeFlag: yup.boolean().nullable(),
    ropeSize: yup.string().nullable(),
    hecFlag: yup.boolean().nullable(),
    crewCount: yup.string().nullable(),
    crewFlag: yup.boolean().nullable(),
    basketFlag: yup.boolean().nullable(),
    hazmatFlag: yup.boolean().nullable(),
    loadWeight: yup.string().nullable(),
    comments: yup.string().nullable(),
  })
  .defined();

export const RequestFormShape = {
  request: RequestInformationFormSchema,
  passengerFlight: RequestPassengerFlightFormSchema.optional(true, null).when(
    'request.flightFlag',
    {
      is: (value) => value === FlightCategory.PatrolPassenger,
      then: RequestPassengerFlightFormSchema.optional(false, null),
    }
  ),
  passengerContractorFlight:
    RequestPassengerContractorFlightFormSchema.optional(true, null).when(
      'request.flightFlag',
      {
        is: (value) => value === FlightCategory.ContractorPatrol,
        then: RequestPassengerContractorFlightFormSchema.required(),
      }
    ),
  constructionFlight: RequestConstructionFormSchema.optional(true, null).when(
    'request.flightFlag',
    {
      is: (value) => value === FlightCategory.ConstructionWork,
      then: RequestConstructionFormSchema.optional(false, null),
    }
  ),
  constructionContractorFlight:
    RequestConstructionContractorFormSchema.optional(true, null).when(
      'request.flightFlag',
      {
        is: (value) => value === FlightCategory.ContractorConstruction,
        then: RequestConstructionContractorFormSchema.optional(false, null),
      }
    ),
  droneFlightSDGE: RequestDroneSDGEFormSchema.optional(true, null).when(
    'request.flightFlag',
    {
      is: (value) => value === FlightCategory.DroneFlightInternal,
      then: RequestDroneSDGEFormSchema.optional(false),
    }
  ),
  droneFlightContractor: RequestDroneContractorFormSchema.optional(true, null).when(
    'request.flightFlag',
    {
      is: (value) => value === FlightCategory.DroneFlightExternal,
      then: RequestDroneContractorFormSchema.optional(false),
    }
  ),
};

export const RequestFormSchema = yup
  .object()
  .shape({ ...RequestFormShape })
  .defined();

export const RequestAdminFormSchema = yup
  .object()
  .shape({
    ...RequestFormShape,
    flightInfo: RequestAdminDetailsFormSchema,
    multiFlightInfo: yup.array().of(RequestPMultiFlightInfoFormSchema),
  })
  .defined();

export const RequestSchema = yup
  .object({
    request: RequestInformationSchema,
    passengerFlight: RequestPassengerFlightSchema.optional(true, null).when(
      'request.flightFlag',
      {
        is: (value) => value === FlightCategory.PatrolPassenger,
        then: RequestPassengerFlightSchema.optional(false, null),
      }
    ),
    passengerContractorFlight:
      RequestPassengerContractorFlightSchema.nullable(),
    constructionFlight: RequestConstructionSchema.optional(true, null).when(
      'request.flightFlag',
      {
        is: (value) => value === FlightCategory.ConstructionWork,
        then: RequestConstructionSchema.optional(false, null),
      }
    ),
    constructionContractorFlight: RequestConstructionContractorSchema.optional(
      true,
      null
    ).when('request.flightFlag', {
      is: (value) => value === FlightCategory.ContractorConstruction,
      then: RequestConstructionContractorSchema.optional(false, null),
    }),
    droneFlightSDGE: RequestDroneSDGESchema.optional(true, null).when(
      'request.flightFlag',
      {
        is: (value) => value === FlightCategory.DroneFlightInternal,
        then: RequestDroneSDGESchema.optional(false),
      }
    ),
    droneFlightContractor: RequestDroneContractorSchema.optional(true, null).when(
      'request.flightFlag',
      {
        is: (value) => value === FlightCategory.DroneFlightExternal,
        then: RequestDroneContractorSchema.optional(false),
      }
    ),
    flightInfo: RequestAdminDetailsSchema.optional(true, null).nullable(),
    multiFlightInfo: yup.array().of(RequestPMultiFlightInfoSchema.clone()).nullable(),
  })
  .defined();

const RequestShape = {
  accounting: yup.array().of(yup.object(AccountingListShape).defined()).defined(),
  attachmentsCount: yup.number().nullable(),
  fieldCompany: yup.string().nullable(),
  flightCategory: yup.string().nullable(),
  flightCategoryKey: yup.number().nullable(),
  flightDate: yup.date().nullable(),
  flightEndDate: yup.date().nullable(),
  flightNo: yup.string().nullable(),
  flightTime: yup.string().nullable(),
  flightTimeType: yup.string().nullable(),
  flightType: yup.string().nullable(),
  location: yup.string().nullable(),
  operatorId: yup.number(),
  operatorName: yup.string().nullable(),
  requestId: yup.number().required(),
  requestDate: yup.date().nullable(),
  requestStatus: yup.string().nullable(),
  requestorDistrict: yup.string().nullable(),
  requestorName: yup.string().nullable(),
  requestorNo: yup.string().nullable(),
  pocNo: yup.string().nullable(),
  pocName: yup.string().nullable(),
  pocDept: yup.string().nullable(),
  pocEmail: yup.string().nullable(),
  pocDistrict: yup.string().nullable(),
  pocPhone: yup.string().nullable(),
  pocJobTitle: yup.string().nullable(),
  pocManagerName: yup.string().nullable(),
  pocManagerEmail: yup.string().nullable(),
  pocCompany: yup.string().nullable(),
  tailNumber: yup.string().nullable(),
  tarStatus: yup.string().nullable(),
  pilot: yup.string().nullable(),
  flightHours: yup.string().nullable(),
  standByHours: yup.string().nullable(),
  costCenter: yup.string().nullable(),
  workOrder: yup.string().nullable(),
  workOrder2: yup.string().nullable(),
  workOrder3: yup.string().nullable(),
  workOrder4: yup.string().nullable(),
  ioNo: yup.string().nullable(),
  ioNo2: yup.string().nullable(),
  ioNo3: yup.string().nullable(),
  ioNo4: yup.string().nullable(),
  planCategory: yup.string().nullable(),
  planCategory2: yup.string().nullable(),
  planCategory3: yup.string().nullable(),
  planCategory4: yup.string().nullable(),
  flightCost: yup.string().nullable(),
  observedFlag: yup.string().nullable(),
  contractorMissionFlag: yup.string().nullable(),
  riskScore: yup.string().nullable(),
};

export const RequestItemSchema = yup
  .object()
  .shape({
    ...RequestShape,
  })
  .defined();

export const RequestListSchema = yup
  .object({
    count: yup
      .object({
        all: yup.number(),
        construction: yup.number(),
        detected: yup.number(),
        passenger: yup.number(),
        drone: yup.number(),
      })
      .defined(),
    rows: yup.array().of(
      yup
        .object()
        .shape({
          ...RequestShape,
        })
        .defined()
    ),
  })
  .defined();

export const CostCenterSchema = yup
  .object({
    Cost_Center: yup
      .object({
        cost_center: yup.string(),
        company_id: yup.string(),
        cost_center_name: yup.string(),
        old_cost_center: yup.string().nullable(),
        cc_mgr_empno: yup.string(),
        cc_status: yup.string(),
        parent_cost_center: yup.string(),
      })
      .defined(),
  })
  .defined();

export const WOIONumberSchema = yup
  .object({
    valid: yup.boolean(),
  })
  .defined();

export const RequestReasonSchema = yup
  .object({
    requestStatus: yup.string().required(),
    reason: yup.string().required(),
  })
  .defined();

export const RequestFlightCostFormSchema = yup.object({
  flightCost: yup.string().required(),
});

export type IRequest = yup.InferType<typeof RequestSchema>;
export type IRequestFilters = yup.InferType<typeof RequestFiltersSchema>;
export type IRequestItem = yup.InferType<typeof RequestItemSchema>;
export type IRequestList = yup.InferType<typeof RequestListSchema>;
export type IRequestForm = yup.InferType<typeof RequestFormSchema>;
export type IRequestAdminForm = yup.InferType<typeof RequestAdminFormSchema>;
export type IRequestPassenger = yup.InferType<typeof RequestPassengerSchema>;
export type IRequestCircuit = yup.InferType<typeof RequestCircuitSchema>;
export type IRequestLandingZone = yup.InferType<
  typeof RequestLandingZoneSchema
>;
export type IRequestFormMethods = Pick<
  UseFormMethods<IRequestForm>,
  'control' | 'errors'
> &
  Partial<
    Pick<UseFormMethods<IRequestForm>, 'setError' | 'clearErrors' | 'formState'>
  >;
export type IRequestAdminFormMethods = Pick<
  UseFormMethods<IRequestAdminForm>,
  'control' | 'errors'
> &
  Partial<
    Pick<
      UseFormMethods<IRequestAdminForm>,
      'setError' | 'clearErrors' | 'formState'
    >
  >;
export type IRequestCostCenter = yup.InferType<typeof CostCenterSchema>;
export type IRequestWorkOrderIONumber = yup.InferType<typeof WOIONumberSchema>;
export type IRequestReason = yup.InferType<typeof RequestReasonSchema>;
export type IStructureId = yup.InferType<typeof StructureIdSchema>;
export type IRequestCNDWONumber = yup.InferType<typeof CNDWONumberSchema>;
